// src/modules/access/accessService.ts
// Serviço para CRUD de Centrais de Acesso no Firestore

import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../core/services/firebase/config';
import { 
  AccessButton, 
  AccessHubConfig, 
  getDefaultButtonsForTenantType,
  adminAccessButtons 
} from './accessConfig';

// ============================================
// ADMIN ACCESS HUB (config global)
// ============================================

const ADMIN_CONFIG_DOC = 'settings/accessHub';

export async function getAdminAccessHub(): Promise<AccessHubConfig> {
  try {
    const docRef = doc(db, ADMIN_CONFIG_DOC);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data() as AccessHubConfig;
    }
    
    // Retorna config padrão se não existir
    return {
      buttons: adminAccessButtons,
      customized: false,
    };
  } catch (error) {
    console.error('Erro ao buscar config admin:', error);
    return {
      buttons: adminAccessButtons,
      customized: false,
    };
  }
}

export async function saveAdminAccessHub(config: AccessHubConfig): Promise<void> {
  try {
    const docRef = doc(db, ADMIN_CONFIG_DOC);
    await setDoc(docRef, {
      ...config,
      customized: true,
      lastModified: serverTimestamp(),
    });
  } catch (error) {
    console.error('Erro ao salvar config admin:', error);
    throw error;
  }
}

// ============================================
// TENANT ACCESS HUB (por cliente)
// ============================================

export async function getTenantAccessHub(
  tenantId: string, 
  tenantType: string = 'pousada'
): Promise<AccessHubConfig> {
  try {
    const docRef = doc(db, `tenants/${tenantId}/settings/accessHub`);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data() as AccessHubConfig;
    }
    
    // Retorna template padrão do tipo se não existir
    return {
      buttons: getDefaultButtonsForTenantType(tenantType),
      customized: false,
    };
  } catch (error) {
    console.error('Erro ao buscar config tenant:', error);
    return {
      buttons: getDefaultButtonsForTenantType(tenantType),
      customized: false,
    };
  }
}

export async function saveTenantAccessHub(
  tenantId: string, 
  config: AccessHubConfig
): Promise<void> {
  try {
    const docRef = doc(db, `tenants/${tenantId}/settings/accessHub`);
    await setDoc(docRef, {
      ...config,
      customized: true,
      lastModified: serverTimestamp(),
    });
  } catch (error) {
    console.error('Erro ao salvar config tenant:', error);
    throw error;
  }
}

// ============================================
// HELPERS
// ============================================

export async function resetTenantAccessHub(
  tenantId: string, 
  tenantType: string
): Promise<void> {
  const defaultConfig: AccessHubConfig = {
    buttons: getDefaultButtonsForTenantType(tenantType),
    customized: false,
  };
  await saveTenantAccessHub(tenantId, defaultConfig);
}

export function addButton(
  config: AccessHubConfig, 
  button: AccessButton
): AccessHubConfig {
  return {
    ...config,
    buttons: [...config.buttons, button].sort((a, b) => a.order - b.order),
    customized: true,
  };
}

export function updateButton(
  config: AccessHubConfig, 
  buttonId: string, 
  updates: Partial<AccessButton>
): AccessHubConfig {
  return {
    ...config,
    buttons: config.buttons.map(btn => 
      btn.id === buttonId ? { ...btn, ...updates } : btn
    ),
    customized: true,
  };
}

export function removeButton(
  config: AccessHubConfig, 
  buttonId: string
): AccessHubConfig {
  return {
    ...config,
    buttons: config.buttons.filter(btn => btn.id !== buttonId),
    customized: true,
  };
}

export function reorderButtons(
  config: AccessHubConfig, 
  buttonIds: string[]
): AccessHubConfig {
  const orderedButtons = buttonIds.map((id, index) => {
    const button = config.buttons.find(btn => btn.id === id);
    return button ? { ...button, order: index + 1 } : null;
  }).filter(Boolean) as AccessButton[];
  
  return {
    ...config,
    buttons: orderedButtons,
    customized: true,
  };
}
